/**
 * Frequent Pages Store
 *
 * Tracks per-user page visits and exposes the top 10 most-visited pages
 * for the "Frequent" quick-nav in the TopNav.
 *
 * Design:
 * - Visits are recorded on every route change (see useFrequentPagesTracker).
 * - Path patterns with route params (e.g. "/settings/plugins/:pluginId/logs")
 *   are normalized to their pattern so distinct entities of the same page
 *   type aggregate together (e.g. visiting multiple plugin detail pages
 *   all count toward "Plugin Detail").
 * - Counts are persisted to localStorage via zustand/persist and scoped
 *   per-user so switching accounts doesn't bleed history across users.
 * - The store is intentionally lightweight (no backend dependency) so it
 *   works offline and during first-paint before the API responds.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const STORAGE_KEY = 'neuro-frequent-pages';
const MAX_PAGES = 10;
const MAX_ENTRIES = 200;

export interface FrequentPageEntry {
  /** Normalized path pattern (e.g. "/settings/plugins/:pluginId/logs") */
  path: string;
  /** Number of times this page has been visited */
  count: number;
  /** ISO timestamp of the most recent visit */
  lastVisitedAt: string;
}

export interface FrequentPagesState {
  /** User id this history belongs to. Set on login / reset on logout. */
  userId: string | null;
  /** Map of normalized path → visit entry. */
  entries: Record<string, FrequentPageEntry>;

  /** Bind the store to a user (called when auth resolves). Clears history if user changes. */
  setUserId: (userId: string | null) => void;
  /**
   * Record a page visit. The caller should pass the canonical route pattern
   * (e.g. "/workflows/:id/edit") resolved via the page / navigation registries
   * so that visits to different entities of the same page type aggregate.
   * Falls back to {@link normalizePath} for paths not in any registry.
   */
  recordVisit: (pattern: string) => void;
  /** Remove a page from the frequent list (e.g. a plugin was uninstalled). */
  forgetPath: (path: string) => void;
  /** Wipe all history for the current user. */
  clear: () => void;
}

/**
 * Normalize a raw pathname to its route pattern.
 *
 * React Router patterns look like "/workflows/:id/edit". We approximate
 * pattern recovery by replacing any path segment that looks like an id
 * (UUID, numeric, long hex, mongo-style) with the placeholder used by
 * the registered page in the page registry.
 *
 * The page registry is consulted at read time (in useFrequentPages) so
 * here we only do the generic normalization: if a segment looks like an
 * identifier (not a known static segment), replace it with ":id". This
 * keeps the storage stable across visits while the registry does the
 * friendly label/icon lookup.
 */
export function normalizePath(rawPath: string): string {
  if (!rawPath) return '/';

  // Drop query string and hash — we only care about the route.
  const cleanPath = rawPath.split('?')[0].split('#')[0];
  if (!cleanPath || cleanPath === '/') return '/';

  const segments = cleanPath.split('/').filter(Boolean);
  if (segments.length === 0) return '/';

  const normalizedSegments = segments.map((segment) => {
    // Decode first so the id heuristic can see the real segment value.
    let decoded = segment;
    try {
      decoded = decodeURIComponent(segment);
    } catch {
      // Malformed percent-encoding — keep the raw segment.
      decoded = segment;
    }
    return looksLikeId(decoded) ? ':id' : decoded;
  });

  return '/' + normalizedSegments.join('/');
}

/**
 * Heuristic: a segment is treated as a dynamic id when it does NOT look
 * like a human-readable slug. Human-readable slugs are made of letters
 * (including accented letters), digits, hyphens, underscores, and spaces
 * AND are short (<= 32 chars). Anything that looks like an identifier —
 * a UUID, a numeric id, a long hex/base64 string, or a Mongo ObjectId —
 * is treated as a dynamic segment and collapsed to ":id".
 *
 * Examples:
 *   "settings"        → static (alphabetic, short)
 *   "Café Menu"       → static (letters + space, short)
 *   "analytics"       → static
 *   "42"              → :id (pure numeric)
 *   "abc12345-..."    → :id (UUID)
 *   "5f8d0d5525..."  → :id (Mongo ObjectId / long hex)
 */
function looksLikeId(segment: string): boolean {
  if (!segment) return false;

  // Pure numeric → id
  if (/^\d+$/.test(segment)) return true;

  // UUID → id
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) {
    return true;
  }

  // Mongo ObjectId (24 hex chars)
  if (/^[0-9a-f]{24}$/i.test(segment)) return true;

  // Long hex / base64-ish identifier (>= 16 chars, mostly non-alpha).
  // Catch long opaque ids that aren't human-readable slugs.
  if (segment.length >= 16 && !/^[a-z][a-z0-9 _-]*$/i.test(segment)) return true;

  // Mixed alphanumeric with digits and length >= 10 (likely a generated id,
  // e.g. "abc123def456" or YouTube-style slugs). Short mixed slugs like
  // "v1" or "p2" stay static.
  if (segment.length >= 10 && /\d/.test(segment) && /[a-z]/i.test(segment)) {
    // But allow "v1", "p2"-style short alphanumeric slugs (length < 10).
    return true;
  }

  return false;
}

/**
 * Pick the top N most-visited pages, breaking ties by most-recent visit.
 */
export function pickTopFrequent(
  entries: Record<string, FrequentPageEntry>,
  limit: number = MAX_PAGES,
): FrequentPageEntry[] {
  return Object.values(entries)
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return b.lastVisitedAt.localeCompare(a.lastVisitedAt);
    })
    .slice(0, limit);
}

function trimEntries(entries: Record<string, FrequentPageEntry>): Record<string, FrequentPageEntry> {
  const values = Object.values(entries);
  if (values.length <= MAX_ENTRIES) return entries;

  // Drop the least-frequently-visited entries beyond the cap.
  const kept = pickTopFrequent(entries, MAX_ENTRIES);
  const next: Record<string, FrequentPageEntry> = {};
  for (const entry of kept) {
    next[entry.path] = entry;
  }
  return next;
}

export const useFrequentPagesStore = create<FrequentPagesState>()(
  persist(
    (set, get) => ({
      userId: null,
      entries: {},

      setUserId: (userId) => {
        const current = get().userId;
        if (current === userId) return;
        // User changed (login / logout / switch) → reset history.
        set({ userId, entries: {} });
      },

      recordVisit: (pattern) => {
        const path = pattern || '/';
        const now = new Date().toISOString();
        const existing = get().entries[path];

        const nextEntries: Record<string, FrequentPageEntry> = {
          ...get().entries,
          [path]: {
            path,
            count: (existing?.count ?? 0) + 1,
            lastVisitedAt: now,
          },
        };

        set({ entries: trimEntries(nextEntries) });
      },

      forgetPath: (path) => {
        const normalized = normalizePath(path);
        const nextEntries = { ...get().entries };
        delete nextEntries[normalized];
        set({ entries: nextEntries });
      },

      clear: () => set({ entries: {} }),
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({
        userId: state.userId,
        entries: state.entries,
      }),
    },
  ),
);

export const FREQUENT_PAGES_LIMIT = MAX_PAGES;